import {
  arbitrum,
  arbitrumSepolia,
  base,
  bsc,
  Chain,
  haqqMainnet,
  mainnet,
  polygon,
} from 'wagmi/chains';

import airdaoLogo from 'assets/images/networks/airdao.png';
import arbitrumLogo from 'assets/images/networks/arbitrum.png';
import baseLogo from 'assets/images/networks/base.png';
//networks
import bscLogo from 'assets/images/networks/bsc.png';
import ethLogo from 'assets/images/networks/eth.svg';
import polygonLogo from 'assets/images/networks/polygon.png';
import reefLogo from 'assets/images/networks/reef.png';
import solanaLogo from 'assets/images/networks/solana.svg';
import unitZeroLogo from 'assets/images/networks/unit-zero.png';
//scans
import bscScanSrc from 'assets/images/scans/bscscan-logo.webp';
import arbiScanSrc from 'assets/images/scans/logo-arbiscan.svg';
import baseScanSrc from 'assets/images/scans/logo-basescan.png';
import ethScanSrc from 'assets/images/scans/logo-etherscan.png';
import haqqScanSrc from 'assets/images/scans/logo-haqqscan.jpg';
import polygonScanSrc from 'assets/images/scans/logo-polygonscan.png';
import solScanSrc from 'assets/images/scans/logo-solscan.png';
import reefScanSrc from 'assets/images/scans/reefScanLogo.png';

export const isTestnet = import.meta.env.VITE_APP_NETWORK_TYPE === 'testnet';

const robinhoodRpcUrl = 'https://rpc.mainnet.chain.robinhood.com';
const robinhoodExplorerUrl = 'https://robinhoodchain.blockscout.com';

export enum EExchange {
  cex = 'cex',
  dex = 'dex',
  sol = 'sol',
}

export const ExchangeRouteBasePath = {
  [EExchange.cex]: 'cex-pair',
  [EExchange.dex]: 'dex-pair',
  [EExchange.sol]: 'sol-pair',
};

export enum ENetwork {
  eth = 'eth',
  bsc = 'bsc',
  polygon = 'polygon',
  arbitrum = 'arbitrum_one',
  base = 'base',
  robinhood = 'robinhood',
  unit0 = 'unit0',
  haqq = 'haqq',
  airdao = 'airdao',
  solana = 'solana',
  reef = 'reef',
}

interface IConfig {
  chainId: number;
  chainName: string;
  wagmiChain: Chain;
  geckoterminalSlug?: string;
}

interface ICommonConfig {
  label: string;
  rpcUrls: string[];
  scanUrl: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  logo: string;
  scanLogo: string;
}

export const unitZeroMainnet: Chain = {
  id: 88811,
  name: 'Unit Zero Mainnet',
  nativeCurrency: {
    name: 'UNIT0',
    symbol: 'UNIT0',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [import.meta.env.VITE_APP_UNIT0_RPC_URL],
    },
  },
  blockExplorers: {
    default: { name: 'Unit Zero Explorer', url: import.meta.env.VITE_APP_UNIT0_SCAN_URL },
  },
};

export const airDaoMainnet: Chain = {
  id: 16718,
  name: 'AirDAO Mainnet',
  nativeCurrency: {
    decimals: 18,
    name: 'AMB',
    symbol: 'AMB',
  },
  rpcUrls: {
    default: {
      http: [import.meta.env.VITE_APP_AIRDAO_RPC_URL],
    },
  },
  blockExplorers: {
    default: { name: 'AirDAO Explorer', url: import.meta.env.VITE_APP_AIRDAO_SCAN_URL },
  },
};

export const solanaMainnet: Chain = {
  id: 101,
  name: 'Solana',
  nativeCurrency: {
    decimals: 9,
    name: 'SOL',
    symbol: 'SOL',
  },
  rpcUrls: {
    default: {
      http: [import.meta.env.VITE_APP_SOLANA_RPC_URL],
    },
  },
  blockExplorers: {
    default: { name: 'Solana Explorer', url: import.meta.env.VITE_APP_SOLANA_SCAN_URL },
  },
};

export const reefMainnet: Chain = {
  id: 13939,
  name: 'Reef',
  nativeCurrency: {
    decimals: 18,
    name: 'Reef',
    symbol: 'REEF',
  },
  rpcUrls: {
    default: {
      http: [import.meta.env.VITE_APP_REEF_RPC_URL],
    },
  },
  blockExplorers: {
    default: { name: 'Reef Explorer', url: import.meta.env.VITE_APP_REEF_SCAN_URL },
  },
};

export const robinhoodMainnet: Chain = {
  id: 4663,
  name: 'Robinhood Chain',
  nativeCurrency: {
    decimals: 18,
    name: 'Ethereum',
    symbol: 'ETH',
  },
  rpcUrls: {
    default: {
      http: [robinhoodRpcUrl],
    },
  },
  blockExplorers: {
    default: { name: 'Robinhood Chain Explorer', url: robinhoodExplorerUrl },
  },
};

const testnets: Partial<Record<ENetwork, IConfig>> = {
  [ENetwork.arbitrum]: {
    chainId: 421614,
    chainName: 'Arbitrum Sepolia testnet',
    wagmiChain: arbitrumSepolia,
  },
};

const mainnets: Record<ENetwork, IConfig> = {
  [ENetwork.bsc]: {
    chainId: 56,
    chainName: 'Binance smart chain',
    wagmiChain: bsc,
    geckoterminalSlug: 'bsc',
  },
  [ENetwork.eth]: {
    chainId: 1,
    chainName: 'Ethereum Mainnet',
    wagmiChain: mainnet,
    geckoterminalSlug: 'eth',
  },
  [ENetwork.polygon]: {
    chainId: 137,
    chainName: 'Polygon Mainnet',
    wagmiChain: polygon,
    geckoterminalSlug: 'polygon_pos',
  },
  [ENetwork.arbitrum]: {
    chainId: 42161,
    chainName: 'Arbitrum One Mainnet',
    wagmiChain: arbitrum,
    geckoterminalSlug: 'arbitrum',
  },
  [ENetwork.base]: {
    chainId: 8453,
    chainName: 'Base Mainnet',
    wagmiChain: base,
    geckoterminalSlug: 'base',
  },
  [ENetwork.robinhood]: {
    chainId: 4663,
    chainName: 'Robinhood Chain',
    wagmiChain: robinhoodMainnet,
    geckoterminalSlug: 'robinhood',
  },
  [ENetwork.unit0]: {
    chainId: 88811,
    chainName: 'Unit Zero Mainnet',
    wagmiChain: unitZeroMainnet,
    geckoterminalSlug: 'units-network',
  },
  [ENetwork.haqq]: {
    chainId: 11235,
    chainName: 'Haqq Network',
    wagmiChain: haqqMainnet,
    geckoterminalSlug: 'haqq-network',
  },
  [ENetwork.airdao]: {
    chainId: 16718,
    chainName: 'AirDAO Mainnet',
    wagmiChain: airDaoMainnet,
    geckoterminalSlug: 'airdao',
  },
  [ENetwork.reef]: {
    chainId: 13939,
    chainName: 'Reef Mainnet',
    wagmiChain: reefMainnet,
    geckoterminalSlug: 'reef',
  },
  [ENetwork.solana]: {
    chainId: 101,
    chainName: 'Solana',
    wagmiChain: solanaMainnet,
    geckoterminalSlug: 'solana',
  },
};

const commonConfigs: Record<ENetwork, ICommonConfig> = {
  [ENetwork.bsc]: {
    label: 'BSC',
    rpcUrls: [import.meta.env.VITE_APP_BSC_RPC_URL],
    scanUrl: import.meta.env.VITE_APP_BSC_SCAN,
    nativeCurrency: {
      name: 'Binance Coin',
      symbol: 'BNB',
      decimals: 18,
    },
    logo: bscLogo,
    scanLogo: bscScanSrc,
  },
  [ENetwork.eth]: {
    label: 'Ethereum',
    rpcUrls: [import.meta.env.VITE_APP_ETH_RPC_URL],
    scanUrl: import.meta.env.VITE_APP_ETH_SCAN,
    nativeCurrency: {
      name: 'Ethereum',
      symbol: 'ETH',
      decimals: 18,
    },
    logo: ethLogo,
    scanLogo: ethScanSrc,
  },
  [ENetwork.polygon]: {
    label: 'Polygon',
    rpcUrls: [import.meta.env.VITE_APP_POLYGON_RPC_URL],
    scanUrl: import.meta.env.VITE_APP_POLYGON_SCAN,
    nativeCurrency: {
      name: 'Polygon',
      symbol: 'POL',
      decimals: 18,
    },
    logo: polygonLogo,
    scanLogo: polygonScanSrc,
  },
  [ENetwork.arbitrum]: {
    label: 'Arbitrum',
    rpcUrls: [import.meta.env.VITE_APP_ARBITRUM_RPC_URL],
    scanUrl: import.meta.env.VITE_APP_ARBI_SCAN_URL,
    nativeCurrency: {
      name: 'Ethereum',
      symbol: 'ETH',
      decimals: 18,
    },
    logo: arbitrumLogo,
    scanLogo: arbiScanSrc,
  },
  [ENetwork.base]: {
    label: 'Base',
    rpcUrls: [import.meta.env.VITE_APP_BASE_RPC_URL],
    scanUrl: import.meta.env.VITE_APP_BASE_SCAN_URL,
    nativeCurrency: {
      name: 'Ethereum',
      symbol: 'ETH',
      decimals: 18,
    },
    logo: baseLogo,
    scanLogo: baseScanSrc,
  },
  [ENetwork.robinhood]: {
    label: 'Robinhood Chain',
    rpcUrls: [robinhoodRpcUrl],
    scanUrl: robinhoodExplorerUrl,
    nativeCurrency: {
      name: 'Ethereum',
      symbol: 'ETH',
      decimals: 18,
    },
    logo: ethLogo,
    scanLogo: ethScanSrc,
  },
  [ENetwork.unit0]: {
    label: 'Unit Zero',
    rpcUrls: [import.meta.env.VITE_APP_UNIT0_RPC_URL],
    scanUrl: import.meta.env.VITE_APP_UNIT0_SCAN_URL,
    nativeCurrency: {
      name: 'UNIT0',
      symbol: 'UNIT0',
      decimals: 18,
    },
    logo: unitZeroLogo,
    scanLogo: unitZeroLogo,
  },
  [ENetwork.haqq]: {
    label: 'Haqq Network',
    rpcUrls: [import.meta.env.VITE_APP_HAQQ_RPC_URL],
    scanUrl: import.meta.env.VITE_APP_HAQQ_SCAN_URL,
    nativeCurrency: {
      name: 'Islamic Coin',
      symbol: 'ISLM',
      decimals: 18,
    },
    logo: haqqScanSrc,
    scanLogo: haqqScanSrc,
  },
  [ENetwork.airdao]: {
    label: 'AirDAO Mainnet',
    rpcUrls: [import.meta.env.VITE_APP_AIRDAO_RPC_URL],
    scanUrl: import.meta.env.VITE_APP_AIRDAO_SCAN_URL,
    nativeCurrency: {
      decimals: 18,
      name: 'AMB',
      symbol: 'AMB',
    },
    logo: airdaoLogo,
    scanLogo: airdaoLogo,
  },
  [ENetwork.reef]: {
    label: 'Reef',
    rpcUrls: [import.meta.env.VITE_APP_REEF_RPC_URL],
    scanUrl: import.meta.env.VITE_APP_REEF_SCAN_URL,
    nativeCurrency: {
      decimals: 18,
      name: 'Reef',
      symbol: 'REEF',
    },
    logo: reefLogo,
    scanLogo: reefScanSrc,
  },
  [ENetwork.solana]: {
    label: 'Solana',
    rpcUrls: [import.meta.env.VITE_APP_SOLANA_RPC_URL],
    scanUrl: import.meta.env.VITE_APP_SOLANA_SCAN_URL,
    nativeCurrency: {
      decimals: 9,
      name: 'SOL',
      symbol: 'SOL',
    },
    logo: solanaLogo,
    scanLogo: solScanSrc,
  },
};

export const supportedWagmiChains = Object.values(ENetwork)
  .filter(el => typeof el === 'string')
  .map(network => ({
    wagmiChain: isTestnet
      ? (testnets[network]?.wagmiChain ?? mainnets[network].wagmiChain)
      : mainnets[network].wagmiChain,
    rpcUrl: commonConfigs[network].rpcUrls[0],
  }));

export const chains = Object.values(ENetwork)
  .filter(el => typeof el === 'string')
  .reduce((acc, network) => {
    return {
      ...acc,
      [network]: isTestnet
        ? (testnets[network]?.chainId ?? mainnets[network].chainId)
        : mainnets[network].chainId,
    };
  }, {}) as Record<ENetwork, number>;

export const web3Configs = Object.values(ENetwork)
  .filter(el => typeof el === 'string')
  .reduce((acc, network) => {
    const conf = isTestnet ? (testnets[network] ?? mainnets[network]) : mainnets[network];
    const commonConf = commonConfigs[network] ?? {};

    return {
      ...acc,
      [network]: {
        ...conf,
        ...commonConf,
      },
    };
  }, {}) as Record<ENetwork, IConfig & ICommonConfig>;

export type IRange = 'ALL' | '4H' | '1M' | '1D' | '1W';
